import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Col, Popconfirm, Row, Table, Select, Radio, RadioChangeEvent, TablePaginationConfig } from 'antd';
import { AutoComplete } from '../../components/autoComplete/autoComplete';
import { ProjectsApi } from 'api/api';
import { ProjectDto } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { Cards } from 'components/cards/frame/cards-frame';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, StyledRadioGroup, TableWrapper, TopToolBox } from 'container/styled';
import { useTranslation } from 'react-i18next';
import { hasPermission } from 'utility/accessibility/hasPermission';
import FeatherIcon from 'feather-icons-react';
import { ProjectForm } from './ProjectForm';
import openNotificationWithIcon from 'utility/notification';
import { ExportButtonPageHeader } from 'components/buttons/export-button/export-button';
import { Link } from 'react-router-dom';
import isSuperAdminUser from 'utility/isSuperAdminUser';
import { CustomTableStyleWrapper } from 'pages/style';
import { filterKeys } from 'constants/constants';

const { Option } = Select;
const projectsApi = new ProjectsApi();

type ModalState = {
  visible: boolean;
  editMode: boolean;
  selectedProject: ProjectDto | null;
};

export const ProjectsPage: React.FC = () => {
  const { t } = useTranslation();
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProjectDto[]>([]);
  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    editMode: false,
    selectedProject: null,
  });
  const [query, setQuery] = useState({
    searchTerm: '',
    pageNumber: 1,
    pageSize: 10,
    filterType: -1,
    isActive: undefined as boolean | undefined,
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await projectsApi.apiProjectsGet(query);
      setData(response.data.items || []);
      setPagination({
        ...pagination,
        current: query.pageNumber,
        total: response.data.totalCount,
      });
    } catch (err) {
      openNotificationWithIcon('error', t('projects:fetch-error', 'Failed to fetch projects'));
    } finally {
      setLoading(false);
    }
  }, [query, t]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const columns = [
    ...(isSuperAdminUser()
      ? [
          {
            title: t('projects:organization', 'Organization'),
            dataIndex: 'organizationName',
            key: 'organizationName',
          },
        ]
      : []),
    {
      title: t('projects:name', 'Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('projects:units', 'Units'),
      dataIndex: 'unitsCount',
      key: 'unitsCount',
    },
    {
      title: t('projects:active-deals', 'Active Deals'),
      dataIndex: 'activeDealsCount',
      key: 'activeDealsCount',
    },
    {
      title: t('projects:contractors', 'Active Contractors'),
      dataIndex: 'activeContractorsCount',
      key: 'activeContractorsCount',
    },
    {
      title: t('projects:status', 'Status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <span className={`status-text ${isActive ? 'active' : 'deactivate'}`}>
          {isActive ? t('global:active', 'Active') : t('global:inactive', 'Inactive')}
        </span>
      ),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'action',
      width: 150,
      render: (record: ProjectDto) => (
        <div className="table-actions">
          {hasPermission('projects:view') && (
            <Link to={`/projects/${record.id}`}>
              <Button className="btn-icon" type="default" shape="circle">
                <FeatherIcon icon="eye" size={16} />
              </Button>
            </Link>
          )}
          {hasPermission('projects:edit') && (
            <Button className="btn-icon" type="primary" shape="circle" onClick={() => handleEdit(record)}>
              <FeatherIcon icon="edit" size={16} />
            </Button>
          )}
          {hasPermission('projects:delete') && (
            <Popconfirm
              title={t('projects:delete-confirm', 'Are you sure you want to delete this project?')}
              onConfirm={() => handleDelete(record.id as number)}
              okText={t('global:yes', 'Yes')}
              cancelText={t('global:no', 'No')}
            >
              <Button className="btn-icon" type="danger" shape="circle">
                <FeatherIcon icon="trash-2" size={16} />
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setQuery(prev => ({ ...prev, searchTerm: value, pageNumber: 1 }));
    }, 300);
  };

  const handleEdit = (project: ProjectDto) => {
    setModalState({
      visible: true,
      editMode: true,
      selectedProject: project,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await projectsApi.apiProjectsIdDelete({ id });
      openNotificationWithIcon('success', t('projects:deleted', 'Project deleted successfully'));
      fetchProjects();
    } catch (err) {
      openNotificationWithIcon('error', t('projects:delete-error', 'Failed to delete project'));
    }
  };

  const handleModalSubmit = async () => {
    setModalState({
      visible: false,
      editMode: false,
      selectedProject: null,
    });
    await fetchProjects();
  };

  const onFilterChange = (e: RadioChangeEvent) => {
    setQuery(prev => ({
      ...prev,
      filterType: e.target.value,
      isActive: e.target.value === -1 ? undefined : e.target.value === 1,
      pageNumber: 1,
      pageSize: 10,
    }));
  };

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t('projects:title', 'Projects')}
          buttons={[
            <ExportButtonPageHeader
              key="1"
              title={t('projects:title', 'Projects')}
              data={data}
              columns={['name', 'organizationName', 'unitsCount', 'activeDealsCount', 'activeContractorsCount']}
            />,
            hasPermission('projects:create') && (
              <Button
                key="2"
                type="primary"
                onClick={() => setModalState({ visible: true, editMode: false, selectedProject: null })}
              >
                <FeatherIcon icon="plus" size={16} />
                {t('projects:create', 'Add Project')}
              </Button>
            ),
          ]}
        />
      </CardToolbox>

      <Main>
        <Cards headless>
          <Row gutter={15}>
            <Col xs={24}>
              <TopToolBox>
                <Row gutter={15}>
                  <Col lg={6} md={6} xs={24}>
                    <div className="table-search-box">
                      <AutoComplete
                        onSearch={handleSearch}
                        width="100%"
                        patterns
                        placeholder={t('projects:search', 'Search projects...')}
                      />
                    </div>
                  </Col>

                  <Col lg={12} md={14} xs={24}>
                    <div className="table-toolbox-menu" style={{ float: 'left', marginLeft: 20 }}>
                      <span className="toolbox-menu-title" style={{ marginRight: 10 }}>
                        {t('users:lable.status', 'Status:')}
                      </span>
                      <StyledRadioGroup onChange={onFilterChange} value={query.filterType}>
                        {filterKeys.map(item => (
                          <Radio.Button key={item.id} value={item.id}>
                            {t(item.key, item.name)}
                          </Radio.Button>
                        ))}
                      </StyledRadioGroup>
                    </div>
                  </Col>
                </Row>
              </TopToolBox>
            </Col>
          </Row>
          <Row>
            <Cards headless>
              <CustomTableStyleWrapper>
                <TableWrapper>
                  <Table
                    loading={loading}
                    dataSource={data}
                    columns={columns}
                    pagination={{
                      current: query.pageNumber,
                      pageSize: query.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                      pageSizeOptions: [10, 50, 100, 1000],
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} ${t('global.of', 'of')} ${total} ${t('global.items', 'items')}`,
                      onChange: (pageNumber, pageSize) => setQuery(prev => ({ ...prev, pageNumber, pageSize })),
                    }}
                  />
                </TableWrapper>
              </CustomTableStyleWrapper>
            </Cards>
          </Row>
        </Cards>
      </Main>

      {modalState.visible && (
        <ProjectForm
          visible={modalState.visible}
          editMode={modalState.editMode}
          project={modalState.selectedProject}
          onCancel={() => setModalState({ visible: false, editMode: false, selectedProject: null })}
          onSuccess={handleModalSubmit}
        />
      )}
    </>
  );
};

export default ProjectsPage;
